<template>
    <div>
        <TableComponent
            :headers="headers"
            :data="data"
            :current-page="currentPage"
            :currentPageSize="params.page_size"
            :total-pages="totalPages"
            :loading="loading"
            :sortColumn="sortColumn"
            :sortOrder="sortOrder"
            :filterOptions="filterFields"
            :totalCounts="totalCounts"
            :messageRefresh="$t('iot_test_report.iot_test_report_msg__refresh')"
            :messagePrevious="
                $t('iot_test_report.iot_test_report_msg__previous')
            "
            :messageNext="$t('iot_test_report.iot_test_report_msg__next')"
            :messageExport="$t('iot_test_report.iot_test_report_msg__export')"
            :messagePageSize="
                $t('iot_test_report.iot_test_report_msg__page_size')
            "
            :messageSearch="
                $t('iot_test_report.iot_test_report_msg__search_placeholder')
            "
            :messageSummary="'iot_test_report.iot_test_report_msg__table_summary'"
            :pageSizes="[10, 20, 50, 100]"
            :searchFields="searchFields"
            :isSearchTimeRestricted="true"
            :messageSearchTimeDays="'iot_test_report.iot_test_report_msg__search_time_range_days'"
            :messageSearchTimeCustom="'iot_test_report.iot_test_report_msg__search_time_range_custom'"
            :messageSearchTimeLimit="{
                key: 'iot_test_report.iot_test_report_msg__search_time_range_limit_days',
                value: timeRange,
            }"
            :selectable="true"
            :selectionMode="'single'"
            :selectionValueKey="'id'"
            :rowClickable="true"
            v-model="selectedItems"
            @on-refresh="refreshData"
            @on-export="exportData"
            @on-change-page="changePage"
            @on-change-page-size="changePageSize"
            @on-filter="filterData"
            @on-text-filter="textFilterData"
            @on-sort="sortData"
            @on-row-click="handleRowClick"
        >
            <template #table-actions>
                <li
                    v-if="rcCollectionEnabled"
                    id="li-rc-detail"
                    class="nav-item"
                >
                    <a
                        class="nav-link"
                        href="#"
                        @click.prevent="showRCTestModal"
                        :class="{ disabled: selectedItems.length !== 1 }"
                    >
                        <i class="fa fa-clock-o"></i>
                        <p>
                            {{
                                $t(
                                    'iot_test_report.iot_test_report_action__rc_detail'
                                )
                            }}
                        </p>
                    </a>
                </li>
            </template>
        </TableComponent>

        <!-- View Details Modal -->
        <ReportDetailModal
            :visible="isDetailModalVisible"
            :title="detailModalTitle"
            @close="closeDetailModal"
        >
            <div class="test-report-container" v-if="selectedReportData">
                <!-- Test Information Section -->
                <div class="report-section">
                    <h6 class="section-title">
                        <i class="fa fa-info-circle"></i>
                        {{
                            $t(
                                'iot_test_report.iot_test_report_section__test_information'
                            )
                        }}
                    </h6>
                    <div class="report-grid">
                        <div class="report-item">
                            <label
                                >{{
                                    $t(
                                        'iot_test_report.iot_test_report_fld__device_model'
                                    )
                                }}:</label
                            >
                            <span class="badge badge-primary">{{
                                selectedReportData.device_model ||
                                $t('iot_test_report.iot_test_report_val__na')
                            }}</span>
                        </div>
                        <div class="report-item">
                            <label
                                >{{
                                    $t(
                                        'iot_test_report.iot_test_report_fld__test_type'
                                    )
                                }}:</label
                            >
                            <span class="badge badge-info">{{
                                selectedReportData.test_type ||
                                $t('iot_test_report.iot_test_report_val__na')
                            }}</span>
                        </div>
                        <div class="report-item">
                            <label
                                >{{
                                    $t(
                                        'iot_test_report.iot_test_report_fld__test_time'
                                    )
                                }}:</label
                            >
                            <span>{{
                                formatDateTime(selectedReportData.test_time)
                            }}</span>
                        </div>
                        <div class="report-item">
                            <label
                                >{{
                                    $t(
                                        'iot_test_report.iot_test_report_fld__upload_time'
                                    )
                                }}:</label
                            >
                            <span>{{
                                formatDateTime(selectedReportData.upload_time)
                            }}</span>
                        </div>
                    </div>
                </div>

                <!-- Device Information Section -->
                <div class="report-section">
                    <h6 class="section-title">
                        <i class="fa fa-tablet"></i>
                        {{
                            $t(
                                'iot_test_report.iot_test_report_section__device_information'
                            )
                        }}
                    </h6>
                    <div class="report-grid">
                        <div class="report-item">
                            <label
                                >{{
                                    $t(
                                        'iot_test_report.iot_test_report_fld__device_sn'
                                    )
                                }}:</label
                            >
                            <span>{{
                                selectedReportData.device_sn ||
                                $t('iot_test_report.iot_test_report_val__na')
                            }}</span>
                        </div>
                        <div class="report-item">
                            <label
                                >{{
                                    $t(
                                        'iot_test_report.iot_test_report_fld__device_name'
                                    )
                                }}:</label
                            >
                            <span>{{
                                selectedReportData.device_name ||
                                $t('iot_test_report.iot_test_report_val__na')
                            }}</span>
                        </div>
                        <div class="report-item">
                            <label
                                >{{
                                    $t(
                                        'iot_test_report.iot_test_report_fld__account'
                                    )
                                }}:</label
                            >
                            <span>{{
                                selectedReportData.account ||
                                $t('iot_test_report.iot_test_report_val__na')
                            }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </ReportDetailModal>

        <!-- RC Test Modal -->
        <ModalComponent
            :visible="isRCTestModalVisible"
            :title="
                $t(
                    'iot_test_report.iot_test_report_title__rc_test_configuration'
                )
            "
            @close="closeRCTestModal"
        >
            <form>
                <div class="form-group">
                    <div class="form-check">
                        <label class="form-check-label">
                            <input
                                type="checkbox"
                                class="form-check-input"
                                v-model="rcTestForm.isTestable"
                            />
                            <span class="form-check-sign"></span>
                            {{
                                $t(
                                    'iot_test_report.iot_test_report_lbl__is_rc_testable'
                                )
                            }}
                        </label>
                    </div>
                </div>

                <div v-if="rcTestForm.isTestable" class="form-group">
                    <label for="realRCTime">
                        {{
                            $t(
                                'iot_test_report.iot_test_report_lbl__real_rc_time'
                            )
                        }}:
                    </label>
                    <input
                        type="number"
                        class="form-control"
                        id="realRCTime"
                        v-model="rcTestForm.realRCTime"
                        :placeholder="
                            $t(
                                'iot_test_report.iot_test_report_ph__enter_rc_time'
                            )
                        "
                        min="0"
                        step="1"
                    />
                </div>

                <div v-if="!rcTestForm.isTestable" class="form-group">
                    <label for="untestableReason">
                        {{
                            $t(
                                'iot_test_report.iot_test_report_lbl__reason_untestable'
                            )
                        }}:
                    </label>
                    <select
                        class="form-control"
                        id="untestableReason"
                        v-model="rcTestForm.reason"
                    >
                        <option value="">
                            {{
                                $t(
                                    'iot_test_report.iot_test_report_opt__reason_select'
                                )
                            }}
                        </option>
                        <option
                            :value="
                                $t(
                                    'iot_test_report.iot_test_report_opt__reason_battery_unable_to_recharge'
                                )
                            "
                        >
                            {{
                                $t(
                                    'iot_test_report.iot_test_report_opt__reason_battery_unable_to_recharge'
                                )
                            }}
                        </option>
                        <option
                            :value="
                                $t(
                                    'iot_test_report.iot_test_report_opt__reason_ocv_less_than_10_5v'
                                )
                            "
                        >
                            {{
                                $t(
                                    'iot_test_report.iot_test_report_opt__reason_ocv_less_than_10_5v'
                                )
                            }}
                        </option>
                        <option value="other">
                            {{
                                $t(
                                    'iot_test_report.iot_test_report_opt__reason_other_manual'
                                )
                            }}
                        </option>
                    </select>
                </div>

                <div
                    v-if="
                        !rcTestForm.isTestable && rcTestForm.reason === 'other'
                    "
                    class="form-group"
                >
                    <label for="manualReason">
                        {{
                            $t(
                                'iot_test_report.iot_test_report_lbl__manual_reason'
                            )
                        }}:
                    </label>
                    <textarea
                        class="form-control"
                        id="manualReason"
                        v-model="rcTestForm.manualReason"
                        :placeholder="
                            $t(
                                'iot_test_report.iot_test_report_ph__manual_reason'
                            )
                        "
                        rows="3"
                    ></textarea>
                </div>

                <div class="mt-2 gap-2 d-md-flex justify-content-md-start">
                    <button
                        type="button"
                        class="btn btn-primary"
                        @click="submitRCTest"
                        :disabled="!isRCFormValid"
                    >
                        {{ $t('iot_test_report.iot_test_report_btn__submit') }}
                    </button>
                    <button
                        type="button"
                        class="btn btn-secondary"
                        @click="closeRCTestModal"
                    >
                        {{ $t('iot_test_report.iot_test_report_btn__cancel') }}
                    </button>
                </div>
            </form>
        </ModalComponent>
    </div>
</template>

<script>
import { ref, onMounted, watch, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import TableComponent from '@/components/TableComponent.vue';
import ModalComponent from '@/components/ModalComponent.vue';
import ReportDetailModal from '@/components/reports/ReportDetailModal.vue';
import Swal from 'sweetalert2';

// TODO: Implement these API methods to match your backend endpoints
// Suggested file: src/api/iot-test-records.js
import {
    fetchIOTTestRecordsFromAPI,
    fetchIOTConfig,
    exportIOTTestRecordsFromAPI,
    partialUpdateIOTTestRecord,
} from '@/api/iot-test-records';

const getDaysAgoFormattedDateRange = daysAgo => {
    const date = new Date();
    const datePast = new Date(date);
    datePast.setDate(datePast.getDate() - daysAgo);
    return {
        gte: datePast.toISOString(),
        lte: date.toISOString(),
    };
};

function calculateDaysDifference(startDate, endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const dayDifference = (end - start) / (1000 * 60 * 60 * 24);
    return dayDifference;
}

function adjustQueryDate(queryDate) {
    const dateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/;
    const dateTimeRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/;

    if (dateTimeRegex.test(queryDate)) {
        return queryDate;
    } else if (dateOnlyRegex.test(queryDate)) {
        const date = new Date(queryDate);
        date.setDate(date.getDate() + 1);

        const maxDate = new Date();
        maxDate.setDate(maxDate.getDate() + 3);

        if (date > maxDate) {
            date.setTime(maxDate.getTime());
        }

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    } else {
        return queryDate;
    }
}

export default {
    components: {
        TableComponent,
        ModalComponent,
        ReportDetailModal,
    },
    setup() {
        const route = useRoute();
        const { t } = useI18n();

        const columnOrder = ref([]);
        const headers = ref([]);
        const data = ref([]);
        const pageSize = ref(10);
        const currentPage = ref(1);
        const totalPages = ref(1);
        const totalCounts = ref(0);
        const filter = ref('');
        const filterFields = ref([]);
        const searchFields = ref([]);
        const sortColumn = ref('');
        const sortOrder = ref('desc');
        const loading = ref(false);
        const params = ref({});
        const timeRange = ref(180);
        const initQueryDate = ref(7);
        const filterTimeLte = ref('');
        const selectedItems = ref([]);
        const isDetailModalVisible = ref(false);
        const isRCTestModalVisible = ref(false);
        const selectedReportData = ref(null);
        const rcCollectionEnabled = ref(false);
        const rcTestForm = ref({
            isTestable: true,
            realRCTime: null,
            reason: '',
            manualReason: '',
        });

        const getFieldLabel = (key, deviceModel) => {
            if (deviceModel) {
                const modelKey = `iot_test_report.${deviceModel}_iot_test_report_fld__${key}`;
                const modelLabel = t(modelKey);
                if (modelLabel !== modelKey) {
                    return modelLabel;
                }
            }

            const commonKey = `iot_test_report.iot_test_report_fld__${key}`;
            const commonLabel = t(commonKey);
            if (commonLabel !== commonKey) {
                return commonLabel;
            }

            return key;
        };

        const detailModalTitle = computed(() => {
            if (selectedReportData.value) {
                const deviceModelLabel =
                    selectedReportData.value.device_model ||
                    t('iot_test_report.iot_test_report_val__na');
                return `${t(
                    'iot_test_report.iot_test_report_title__detail'
                )} - ${deviceModelLabel}`;
            }
            return t('iot_test_report.iot_test_report_title__detail');
        });

        const isRCFormValid = computed(() => {
            if (rcTestForm.value.isTestable) {
                return (
                    rcTestForm.value.realRCTime !== null &&
                    rcTestForm.value.realRCTime >= 0
                );
            } else {
                if (rcTestForm.value.reason === 'other') {
                    return rcTestForm.value.manualReason.trim() !== '';
                }
                return rcTestForm.value.reason !== '';
            }
        });

        const buildParams = () => {
            const newParams = {
                page: currentPage.value,
                page_size: pageSize.value,
                search: filter.value,
            };
            return newParams;
        };

        const applyRouteParams = () => {
            const dm = route.query.device_model ?? route.params.device_model;
            const tt = route.query.test_type ?? route.params.test_type;

            if (dm !== undefined && dm !== '') {
                params.value.device_model = dm;
            } else {
                delete params.value.device_model;
            }

            if (tt !== undefined && tt !== '') {
                params.value.test_type = tt;
            } else {
                delete params.value.test_type;
            }

            // Optional: prefill filter fields UI if present in config
            const setFilterValue = (key, value) => {
                const fld = filterFields.value.find(f => f.key === key);
                if (fld) fld.value = value ?? '';
            };
            setFilterValue('device_model', params.value.device_model);
            setFilterValue('test_type', params.value.test_type);
        };

        const loadConfig = async () => {
            const configParams = {
                device_model: params.value.device_model,
                test_type: params.value.test_type,
            };
            const response = await fetchIOTConfig(configParams);
            const sortableColumns = response.ordering_fields || [];
            columnOrder.value = response.display_fields || [];
            headers.value = columnOrder.value.map(key => ({
                text: getFieldLabel(key, params.value.device_model),
                value: key,
                sortable: sortableColumns.includes(key),
            }));
            const responseFilterFields = response.filter_fields || {};
            filterFields.value = Object.keys(responseFilterFields).map(key => ({
                key,
                label: getFieldLabel(key, params.value.device_model),
                type: responseFilterFields[key].type,
                value: '',
                start: '',
                end: '',
                options: responseFilterFields[key].items || [],
            }));
            if (response.upload_time_range)
                timeRange.value = parseInt(response.upload_time_range);
            if (response.search_fields) {
                searchFields.value = response.search_fields.map(k => ({
                    key: k,
                    label: getFieldLabel(k, params.value.device_model),
                }));
            }
            if (response.rc_real_time_collection_enabled !== undefined) {
                rcCollectionEnabled.value =
                    response.rc_real_time_collection_enabled;
            }
        };

        const fetchData = async () => {
            if (
                params.value.upload_time__gte &&
                params.value.upload_time__lte &&
                calculateDaysDifference(
                    params.value.upload_time__gte,
                    params.value.upload_time__lte
                ) >
                    timeRange.value + 7
            ) {
                console.log('Time range exceeds limit');
                return;
            }

            if (params.value.upload_time__lte) {
                const queryDate =
                    filterTimeLte.value || params.value.upload_time__lte;
                params.value.upload_time__lte = adjustQueryDate(queryDate);
            }

            loading.value = true;
            try {
                const response = await fetchIOTTestRecordsFromAPI(params.value);
                data.value = response.results;
                currentPage.value = response.current_page;
                totalCounts.value = response.total_count;
                totalPages.value = response.total_pages;
            } catch (error) {
                console.error(error);
            } finally {
                loading.value = false;
            }
        };

        const changePage = async page => {
            if (page >= 1 && page <= totalPages.value) {
                params.value.page = page;
                await fetchData();
            }
        };

        const changePageSize = async size => {
            params.value.page_size = size;
            await fetchData();
        };

        const filterData = async filter => {
            if (filter.type === 'datetime') {
                params.value[`${filter.key}__gte`] = filter.start;
                params.value[`${filter.key}__lte`] = filter.end;
                filterTimeLte.value = filter.end;
            } else {
                params.value[filter.key] = filter.value;
            }
            params.value.page = 1;
            await fetchData();
        };

        const sortData = async (columnIndex, order) => {
            sortColumn.value = columnIndex;
            sortOrder.value = order;
            params.value.ordering = `${sortOrder.value === 'desc' ? '-' : ''}${
                sortColumn.value
            }`;
            params.value.page = 1;
            await fetchData();
        };

        const textFilterData = async search => {
            if (search.field !== '') {
                params.value.search_fields = search.field;
            } else {
                params.value.search_fields = searchFields.value
                    .map(s => s.key)
                    .join(',');
            }
            params.value.search = search.text;
            params.value.page = 1;
            sortColumn.value = '';
            sortOrder.value = 'desc';
            delete params.value.ordering;
            await fetchData();
        };

        const exportData = async type => {
            const response = await exportIOTTestRecordsFromAPI(
                params.value,
                type
            );
            if (type === 'csv') {
                const blob = new Blob([response], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'iot-test-records.csv';
                a.click();
                window.URL.revokeObjectURL(url);
            } else if (type === 'excel') {
                const blob = new Blob([response], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'iot-test-records.xlsx';
                a.click();
                window.URL.revokeObjectURL(url);
            }
        };

        onMounted(async () => {
            Object.assign(params.value, buildParams());
            applyRouteParams(); // set params from route first
            await loadConfig(); // then load config with those params

            if (
                !params.value.upload_time__gte ||
                !params.value.upload_time__lte
            ) {
                const { gte, lte } = getDaysAgoFormattedDateRange(
                    initQueryDate.value
                );
                params.value.upload_time__gte = gte;
                params.value.upload_time__lte = lte;
            }
            await fetchData();
        });

        // React to both params and query changes
        watch(
            () => [
                route.params.device_model,
                route.params.test_type,
                route.query.device_model,
                route.query.test_type,
            ],
            async () => {
                applyRouteParams();
                await loadConfig();
                params.value.page = 1;
                await fetchData();
            }
        );

        const handleRowClick = row => {
            // Toggle selection on row click
            const itemId = row[selectedItems.value.length > 0 ? 'id' : 'id'];
            const index = selectedItems.value.indexOf(itemId);

            if (index > -1) {
                // If already selected, deselect it
                selectedItems.value = [];
            } else {
                // If not selected, select it (single selection mode)
                selectedItems.value = [itemId];
            }
        };

        const showSelectedReports = () => {
            if (selectedItems.value.length === 0) {
                Swal.fire({
                    title: t(
                        'iot_test_report.iot_test_report_msg__no_selection_title'
                    ),
                    text: t(
                        'iot_test_report.iot_test_report_msg__no_selection_view_detail'
                    ),
                    icon: 'warning',
                    confirmButtonText: t(
                        'iot_test_report.iot_test_report_btn__ok'
                    ),
                });
                return;
            }

            const firstSelectedId = selectedItems.value[0];
            const reportData = data.value.find(
                item => item.id === firstSelectedId
            );

            if (reportData) {
                selectedReportData.value = reportData;
                isDetailModalVisible.value = true;
            }
        };

        const closeDetailModal = () => {
            isDetailModalVisible.value = false;
        };

        const showRCTestModal = () => {
            if (selectedItems.value.length === 0) {
                Swal.fire({
                    title: t(
                        'iot_test_report.iot_test_report_msg__no_selection_title'
                    ),
                    text: t(
                        'iot_test_report.iot_test_report_msg__no_selection_rc_test'
                    ),
                    icon: 'warning',
                    confirmButtonText: t(
                        'iot_test_report.iot_test_report_btn__ok'
                    ),
                });
                return;
            }

            // Reset form
            rcTestForm.value = {
                isTestable: false,
                realRCTime: null,
                reason: '',
                manualReason: '',
            };

            isRCTestModalVisible.value = true;
        };

        const closeRCTestModal = () => {
            isRCTestModalVisible.value = false;
        };

        const submitRCTest = async () => {
            if (!isRCFormValid.value) {
                return;
            }

            const selectedId = selectedItems.value[0];
            const selectedRecord = data.value.find(
                item => item.id === selectedId
            );

            if (!selectedRecord) {
                Swal.fire({
                    title: t(
                        'iot_test_report.iot_test_report_msg__error_title'
                    ),
                    text: t(
                        'iot_test_report.iot_test_report_msg__error_selected_record_not_found'
                    ),
                    icon: 'error',
                    confirmButtonText: t(
                        'iot_test_report.iot_test_report_btn__ok'
                    ),
                });
                return;
            }

            const patchData = rcTestForm.value.isTestable
                ? {
                      is_rc_testable: true,
                      real_rc_time: rcTestForm.value.realRCTime,
                      rc_testable_reason: null,
                  }
                : {
                      is_rc_testable: false,
                      real_rc_time: null,
                      rc_testable_reason:
                          rcTestForm.value.reason === 'other'
                              ? rcTestForm.value.manualReason
                              : rcTestForm.value.reason,
                  };

            try {
                await partialUpdateIOTTestRecord({
                    device_model: params.value.device_model,
                    id: selectedId,
                    data: patchData,
                });

                await Swal.fire({
                    title: t(
                        'iot_test_report.iot_test_report_msg__success_title'
                    ),
                    text: t(
                        'iot_test_report.iot_test_report_msg__success_rc_submitted'
                    ),
                    icon: 'success',
                    confirmButtonText: t(
                        'iot_test_report.iot_test_report_btn__ok'
                    ),
                });

                closeRCTestModal();
                selectedItems.value = [];
                await fetchData(); // Refresh table data
            } catch (error) {
                console.error('Failed to submit RC test:', error);
                Swal.fire({
                    title: t(
                        'iot_test_report.iot_test_report_msg__error_title'
                    ),
                    text: t(
                        'iot_test_report.iot_test_report_msg__error_rc_submit'
                    ),
                    icon: 'error',
                    confirmButtonText: t(
                        'iot_test_report.iot_test_report_btn__ok'
                    ),
                });
            }
        };

        const formatDateTime = dateTimeString => {
            if (!dateTimeString)
                return t('iot_test_report.iot_test_report_val__na');
            const date = new Date(dateTimeString);
            return date.toLocaleString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
            });
        };

        const refreshData = async () => {
            const currentTime = new Date();
            const fourHoursInSeconds = 4 * 60 * 60;
            if (params.value.upload_time__lte) {
                const uploadTimeLte = new Date(params.value.upload_time__lte);
                const diffSec = Math.abs((currentTime - uploadTimeLte) / 1000);
                if (diffSec <= fourHoursInSeconds) {
                    params.value.upload_time__lte = currentTime.toISOString();
                }
            } else {
                params.value.upload_time__lte = currentTime.toISOString();
            }
            await fetchData();
        };

        return {
            headers,
            data,
            currentPage,
            totalPages,
            totalCounts,
            filterFields,
            searchFields,
            sortColumn,
            sortOrder,
            loading,
            params,
            fetchData,
            refreshData,
            changePage,
            changePageSize,
            filterData,
            sortData,
            textFilterData,
            exportData,
            timeRange,
            selectedItems,
            isDetailModalVisible,
            isRCTestModalVisible,
            selectedReportData,
            detailModalTitle,
            handleRowClick,
            showSelectedReports,
            closeDetailModal,
            showRCTestModal,
            closeRCTestModal,
            submitRCTest,
            formatDateTime,
            rcTestForm,
            isRCFormValid,
            rcCollectionEnabled,
        };
    },
};
</script>
